import { Fragment, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { LocationsPanel } from './LocationsTab';
import { Eye, Loader2, Network } from 'lucide-react';

import {
  useDeliverySchedules,
  useReminderPreview,
  useSaveDeliverySchedule,
  type RemindersDraft,
  type ScheduleDraft,
} from '@/hooks/admin/useDeliverySchedule';
import {
  DAY_NAMES,
  DEFAULT_CUTOFF_TIME,
  describeSchedule,
  fmtCutoffTime,
  fmtScheduleDate,
  parseDeliverySchedule,
} from '@/lib/deliverySchedule';
import {
  REMINDER_TIME_ZONES,
  defaultReminders,
  describeReminders,
  parseOrderReminders,
} from '@/lib/orderReminders';
import { useCustomerDetailContext } from '@/pages/admin/AdminCustomerDetail';
import { Button } from '@/components/ui/button';
import {
  ActionsTd,
  ExpandedRow,
  PayTable,
  PayTabs,
  Td,
  Th,
  type PayTab,
} from '@/components/admin/PaymentsLayout';
import { cn } from '@/lib/utils';

/**
 * Delivery days + order reminders — two sub-tabs of the customer's
 * Locations & deliveries page, one job each:
 *
 *   Delivery days    when this account RECEIVES deliveries (days, every week
 *                    or every two, the order cutoff). Drives the Cart's
 *                    default date and EDI PO date snapping.
 *   Order reminders  when it is REMINDED to order: the day before each
 *                    delivery, or on the weekdays picked, at a time in a
 *                    time zone ("every Monday at 2:00 PM ET").
 *
 * Chains (Sky, 2026-11-02): the delivery day is never one setting for the
 * whole chain. The master keeps its own, and every store — its own account —
 * keeps its own, edited right here from the master's page in the Stores
 * table. A multi-location account's locations can override the same way.
 */

type TabKey = 'locations' | 'days' | 'reminders';
const TAB_KEYS: TabKey[] = ['locations', 'days', 'reminders'];

const DAY_ORDER = [1, 2, 3, 4, 5, 6, 0] as const;
const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const inputCls =
  'h-9 rounded-lg border border-brix-border bg-brix-surface px-3 text-sm text-brix-ink placeholder:text-brix-muted/60 focus:outline-none focus:border-brix-primary/50 disabled:opacity-50';

/** Half-hour slots 5:00 AM → 9:00 PM: the cron runs every 30 minutes. */
const TIME_SLOTS = Array.from({ length: 33 }, (_, i) => {
  const mins = 5 * 60 + i * 30;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  const value = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  const label = `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
  return { value, label };
});

/* ================================================================== */
/* Page                                                                 */
/* ================================================================== */

/**
 * The customer's Locations & deliveries page (route `locations`): where they
 * are, when they get deliveries, and when they're reminded to order — one
 * page, three sub-tabs (Sky, 2026-11-02: "make deliveries and locations the
 * same page"). The open sub-tab is in the URL (`?view=days`), so links can
 * land on it.
 */
export default function LocationsDeliveriesTab() {
  const { customer } = useCustomerDetailContext();
  const { data, isLoading, error } = useDeliverySchedules(customer.id);
  const [params, setParams] = useSearchParams();
  const view = params.get('view') as TabKey | null;
  const tab: TabKey = view && TAB_KEYS.includes(view) ? view : 'locations';
  const setTab = (k: TabKey) =>
    setParams(
      (p) => {
        const next = new URLSearchParams(p);
        if (k === 'locations') next.delete('view');
        else next.set('view', k);
        return next;
      },
      { replace: true },
    );

  const ownSched = parseDeliverySchedule(data?.customer.delivery_schedule);
  const ownRem = parseOrderReminders(data?.customer.order_reminders);
  const stores = (data?.stores ?? []).filter((s) => s.active);
  const locations = (data?.locations ?? []).filter((l) => l.active);
  const showLocations =
    locations.length > 1 ||
    locations.some((l) => parseDeliverySchedule(l.delivery_schedule) || parseOrderReminders(l.order_reminders));

  const storesWithoutDays = stores.filter((s) => !parseDeliverySchedule(s.delivery_schedule)).length;

  const activeLocations = customer.locations.filter((l) => l.active).length;
  const tabs: Array<PayTab<TabKey>> = [
    {
      key: 'locations',
      label: 'Locations',
      tag: { text: String(activeLocations) },
    },
    {
      key: 'days',
      label: 'Delivery days',
      tag: ownSched
        ? { text: ownSched.enabled ? describeSchedule(ownSched) : 'Off', tone: ownSched.enabled ? 'muted' : 'amber' }
        : { text: 'Not set', tone: 'amber' },
      alert: storesWithoutDays,
    },
    {
      key: 'reminders',
      label: 'Order reminders',
      tag: ownRem?.enabled ? { text: 'On' } : { text: 'Off', tone: 'amber' },
    },
  ];

  if (tab === 'locations') {
    return (
      <div className="space-y-4">
        <PayTabs tabs={tabs} value={tab} onChange={setTab} />
        <LocationsPanel />
      </div>
    );
  }

  if (isLoading || error || !data) {
    return (
      <div className="space-y-4">
        <PayTabs tabs={tabs} value={tab} onChange={setTab} />
        {isLoading ? (
          <p className="flex items-center gap-2 text-sm text-brix-muted">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </p>
        ) : (
          <p className="text-sm text-red-700">
            {error instanceof Error ? error.message : 'Could not load delivery settings.'}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {data.member_of && (
        <div className="flex items-center gap-2 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-[13px] text-sky-900">
          <Network size={14} className="shrink-0" />
          <span>
            Part of{' '}
            <Link to={`/admin/customers/${data.member_of.id}/locations?view=days`} className="font-semibold underline">
              {data.member_of.name}
            </Link>
            . This store keeps its own delivery days and reminders.
          </span>
        </div>
      )}

      <PayTabs tabs={tabs} value={tab} onChange={setTab} />

      {tab === 'days' && (
        <div className="space-y-4">
          <Section
            title="This account"
            desc="When this account receives deliveries. New orders default to the next delivery day; emailed POs snap onto it."
          >
            <ScheduleForm customerId={customer.id} stored={data.customer.delivery_schedule} />
          </Section>

          {stores.length > 0 && (
            <Section
              title={`Stores · ${stores.length}`}
              desc="Each store has its own delivery days. Nothing here copies from the account above."
            >
              <SettingsTable
                kind="days"
                rows={stores.map((s) => ({
                  key: s.id,
                  name: s.name,
                  code: s.store_code,
                  href: `/admin/customers/${s.id}/locations?view=${tab}`,
                  targetCustomerId: s.id,
                  locationId: null,
                  schedule: s.delivery_schedule,
                  reminders: s.order_reminders,
                  inherits: false,
                }))}
                pageCustomerId={customer.id}
              />
            </Section>
          )}

          {showLocations && (
            <Section
              title={`Locations · ${locations.length}`}
              desc="A location without its own days uses this account's."
            >
              <SettingsTable
                kind="days"
                rows={locations.map((l) => ({
                  key: l.id,
                  name: l.display_name ?? l.id.slice(0, 8),
                  code: l.location_code,
                  href: null,
                  targetCustomerId: customer.id,
                  locationId: l.id,
                  schedule: l.delivery_schedule,
                  reminders: l.order_reminders,
                  inherits: true,
                }))}
                pageCustomerId={customer.id}
                accountSchedule={data.customer.delivery_schedule}
                accountReminders={data.customer.order_reminders}
              />
            </Section>
          )}
        </div>
      )}

      {tab === 'reminders' && (
        <div className="space-y-4">
          <Section
            title="This account"
            desc="When to email a reminder to place an order. It is skipped when an order is already in."
          >
            <RemindersForm
              customerId={customer.id}
              stored={data.customer.order_reminders}
              hasSchedule={Boolean(ownSched?.enabled)}
              cutoffLabel={ownSched ? fmtCutoffTime(ownSched) : null}
            />
          </Section>

          {stores.length > 0 && (
            <Section title={`Stores · ${stores.length}`} desc="Each store has its own reminders.">
              <SettingsTable
                kind="reminders"
                rows={stores.map((s) => ({
                  key: s.id,
                  name: s.name,
                  code: s.store_code,
                  href: `/admin/customers/${s.id}/locations?view=${tab}`,
                  targetCustomerId: s.id,
                  locationId: null,
                  schedule: s.delivery_schedule,
                  reminders: s.order_reminders,
                  inherits: false,
                }))}
                pageCustomerId={customer.id}
              />
            </Section>
          )}

          {showLocations && (
            <Section
              title={`Locations · ${locations.length}`}
              desc="A location without its own reminders uses this account's."
            >
              <SettingsTable
                kind="reminders"
                rows={locations.map((l) => ({
                  key: l.id,
                  name: l.display_name ?? l.id.slice(0, 8),
                  code: l.location_code,
                  href: null,
                  targetCustomerId: customer.id,
                  locationId: l.id,
                  schedule: l.delivery_schedule,
                  reminders: l.order_reminders,
                  inherits: true,
                }))}
                pageCustomerId={customer.id}
                accountSchedule={data.customer.delivery_schedule}
                accountReminders={data.customer.order_reminders}
              />
            </Section>
          )}

          <PreviewSection customerId={customer.id} />
        </div>
      )}
    </div>
  );
}

/* ================================================================== */
/* Layout pieces (same as the Company page)                             */
/* ================================================================== */

function Section({
  title,
  desc,
  actions,
  children,
}: {
  title: string;
  desc?: string;
  actions?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className="rounded-xl border border-brix-border bg-brix-surface p-5 space-y-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0 max-w-2xl flex-1">
          <h2 className="text-base font-semibold text-brix-ink">{title}</h2>
          {desc && <p className="text-xs leading-relaxed text-brix-muted mt-0.5">{desc}</p>}
        </div>
        {actions && <div className="flex shrink-0 flex-wrap items-center gap-2 md:justify-end">{actions}</div>}
      </div>
      {children}
    </div>
  );
}

/** One labelled row of a form: label column on the left, control on the right. */
function FormRow({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div className="grid gap-1.5 sm:grid-cols-[150px_1fr] sm:items-start">
      <div className="pt-2">
        <div className="text-[11px] font-semibold uppercase tracking-tight text-brix-muted">{label}</div>
      </div>
      <div className="space-y-1">
        {children}
        {hint && <p className="text-[11px] text-brix-muted">{hint}</p>}
      </div>
    </div>
  );
}

type TagTone = 'muted' | 'on' | 'off' | 'own';

function Tag({ tone = 'muted', children }: { tone?: TagTone; children: ReactNode }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap',
        tone === 'on' && 'border-emerald-200 bg-emerald-50 text-emerald-800',
        tone === 'off' && 'border-amber-200 bg-amber-50 text-amber-800',
        tone === 'own' && 'border-brix-primary/30 bg-brix-primary-tint text-brix-primary',
        tone === 'muted' && 'border-brix-border bg-brix-surface-alt text-brix-muted',
      )}
    >
      {children}
    </span>
  );
}

function DayChips({ value, onToggle }: { value: string[]; onToggle: (name: string) => void }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {DAY_ORDER.map((idx) => {
        const name = DAY_NAMES[idx]!;
        const on = value.includes(name);
        return (
          <button
            key={idx}
            type="button"
            aria-pressed={on}
            onClick={() => onToggle(name)}
            className={cn(
              'h-9 w-12 rounded-lg border text-xs font-semibold transition-colors',
              on
                ? 'border-brix-primary bg-brix-primary text-white'
                : 'border-brix-border bg-brix-surface text-brix-muted hover:text-brix-ink',
            )}
          >
            {DAY_SHORT[idx]}
          </button>
        );
      })}
    </div>
  );
}

function Segmented<K extends string>({
  value,
  options,
  onChange,
}: {
  value: K;
  options: Array<{ key: K; label: string }>;
  onChange: (k: K) => void;
}) {
  return (
    <div className="inline-flex rounded-lg border border-brix-border bg-brix-surface-alt p-0.5">
      {options.map((o) => (
        <button
          key={o.key}
          type="button"
          aria-pressed={value === o.key}
          onClick={() => onChange(o.key)}
          className={cn(
            'rounded-md px-3 py-1.5 text-xs font-semibold',
            value === o.key ? 'bg-brix-surface text-brix-primary shadow-sm' : 'text-brix-muted hover:text-brix-ink',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function Switch({ on, onChange, label }: { on: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      onClick={() => onChange(!on)}
      className={cn(
        'relative h-6 w-10 shrink-0 rounded-full transition-colors',
        on ? 'bg-brix-primary' : 'bg-slate-300',
      )}
    >
      <span
        className={cn(
          'absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform',
          on && 'translate-x-4',
        )}
      />
    </button>
  );
}

/** Save / Remove / message — always at the bottom-left of a form. */
function FormFooter({
  canSave,
  saving,
  onSave,
  removeLabel,
  onRemove,
  msg,
}: {
  canSave: boolean;
  saving: boolean;
  onSave: () => void;
  removeLabel?: string | null;
  onRemove?: () => void;
  msg: { kind: 'ok' | 'err'; text: string } | null;
}) {
  return (
    <div className="flex flex-wrap items-center gap-3 border-t border-brix-border pt-3">
      <Button variant="primary" size="sm" disabled={!canSave || saving} onClick={onSave}>
        {saving ? <Loader2 size={13} className="animate-spin" /> : null}
        {saving ? 'Saving…' : 'Save'}
      </Button>
      {removeLabel && onRemove && (
        <Button variant="subtle" size="sm" disabled={saving} onClick={onRemove} className="hover:text-red-700">
          {removeLabel}
        </Button>
      )}
      {msg && (
        <span className={cn('text-xs', msg.kind === 'ok' ? 'text-emerald-700' : 'text-red-700')}>{msg.text}</span>
      )}
    </div>
  );
}

/* ================================================================== */
/* Delivery days form                                                   */
/* ================================================================== */

function scheduleDraftFrom(raw: unknown): ScheduleDraft {
  const s = parseDeliverySchedule(raw);
  if (!s) {
    return { enabled: true, days: [], every_n_weeks: 1, anchor_date: null, cutoff_time: DEFAULT_CUTOFF_TIME };
  }
  return {
    enabled: s.enabled,
    days: s.days.map((d) => DAY_NAMES[d]!),
    every_n_weeks: s.every_n_weeks === 2 ? 2 : 1,
    anchor_date: s.anchor_date,
    cutoff_time: s.cutoff_time,
  };
}

function ScheduleForm({
  customerId,
  pageCustomerId,
  locationId = null,
  stored,
  removeLabel = 'Remove delivery days',
  onDone,
}: {
  customerId: string;
  /** The page's account, when saving a store from its master's page. */
  pageCustomerId?: string;
  locationId?: string | null;
  stored: unknown;
  removeLabel?: string;
  onDone?: () => void;
}) {
  const save = useSaveDeliverySchedule(pageCustomerId ?? customerId);
  const [draft, setDraft] = useState<ScheduleDraft>(() => scheduleDraftFrom(stored));
  const [dirty, setDirty] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  useEffect(() => {
    if (!dirty) setDraft(scheduleDraftFrom(stored));
  }, [stored, dirty]);

  const edit = (fn: (d: ScheduleDraft) => ScheduleDraft) => {
    setDraft(fn);
    setDirty(true);
    setMsg(null);
  };

  const canSave = dirty && draft.days.length > 0 && (draft.every_n_weeks !== 2 || Boolean(draft.anchor_date));
  const target = pageCustomerId ? { target_customer_id: customerId } : {};

  const run = (schedule: ScheduleDraft | null, okText: string) =>
    save.mutate(
      { ...target, location_id: locationId, schedule },
      {
        onSuccess: () => {
          setDirty(false);
          setMsg({ kind: 'ok', text: okText });
          onDone?.();
        },
        onError: (e) => setMsg({ kind: 'err', text: e instanceof Error ? e.message : 'Save failed' }),
      },
    );

  return (
    <div className="space-y-3">
      <FormRow label="Delivery days">
        <DayChips
          value={draft.days}
          onToggle={(name) =>
            edit((d) => ({
              ...d,
              days: d.days.includes(name) ? d.days.filter((x) => x !== name) : [...d.days, name],
            }))
          }
        />
      </FormRow>
      <FormRow label="How often">
        <div className="flex flex-wrap items-center gap-3">
          <Segmented
            value={draft.every_n_weeks === 2 ? 'two' : 'one'}
            options={[
              { key: 'one', label: 'Every week' },
              { key: 'two', label: 'Every two weeks' },
            ]}
            onChange={(k) => edit((d) => ({ ...d, every_n_weeks: k === 'two' ? 2 : 1 }))}
          />
          {draft.every_n_weeks === 2 && (
            <label className="flex items-center gap-2 text-xs text-brix-muted">
              Starting
              <input
                type="date"
                className={inputCls}
                value={draft.anchor_date ?? ''}
                onChange={(e) => edit((d) => ({ ...d, anchor_date: e.target.value || null }))}
              />
            </label>
          )}
        </div>
      </FormRow>
      <FormRow label="Order by" hint="Pacific time, the day before each delivery.">
        <input
          type="time"
          className={cn(inputCls, 'w-36')}
          value={draft.cutoff_time}
          onChange={(e) => edit((d) => ({ ...d, cutoff_time: e.target.value || DEFAULT_CUTOFF_TIME }))}
        />
      </FormRow>
      <FormFooter
        canSave={canSave}
        saving={save.isPending}
        onSave={() => run(draft, 'Saved.')}
        removeLabel={parseDeliverySchedule(stored) ? removeLabel : null}
        onRemove={() => {
          if (window.confirm(`${removeLabel}?`)) run(null, 'Removed.');
        }}
        msg={msg}
      />
    </div>
  );
}

/* ================================================================== */
/* Order reminders form                                                 */
/* ================================================================== */

function remindersDraftFrom(raw: unknown): RemindersDraft {
  const r = parseOrderReminders(raw) ?? { ...defaultReminders(), enabled: false };
  return {
    enabled: r.enabled,
    mode: r.mode,
    days: r.days.map((d) => DAY_NAMES[d]!),
    time: r.time,
    time_zone: r.time_zone,
    emails: r.emails,
  };
}

function RemindersForm({
  customerId,
  pageCustomerId,
  locationId = null,
  stored,
  hasSchedule,
  cutoffLabel,
  removeLabel = null,
  onDone,
}: {
  customerId: string;
  pageCustomerId?: string;
  locationId?: string | null;
  stored: unknown;
  hasSchedule: boolean;
  cutoffLabel: string | null;
  removeLabel?: string | null;
  onDone?: () => void;
}) {
  const save = useSaveDeliverySchedule(pageCustomerId ?? customerId);
  const [draft, setDraft] = useState<RemindersDraft>(() => remindersDraftFrom(stored));
  const [emailsText, setEmailsText] = useState(() => remindersDraftFrom(stored).emails.join(', '));
  const [dirty, setDirty] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  useEffect(() => {
    if (dirty) return;
    const d = remindersDraftFrom(stored);
    setDraft(d);
    setEmailsText(d.emails.join(', '));
  }, [stored, dirty]);

  const edit = (fn: (d: RemindersDraft) => RemindersDraft) => {
    setDraft(fn);
    setDirty(true);
    setMsg(null);
  };

  const target = pageCustomerId ? { target_customer_id: customerId } : {};
  const canSave = dirty && (!draft.enabled || draft.mode !== 'weekly' || draft.days.length > 0);
  const perWeek = draft.days.length;

  const run = (reminders: RemindersDraft | null, okText: string) =>
    save.mutate(
      { ...target, location_id: locationId, reminders },
      {
        onSuccess: () => {
          setDirty(false);
          setMsg({ kind: 'ok', text: okText });
          onDone?.();
        },
        onError: (e) => setMsg({ kind: 'err', text: e instanceof Error ? e.message : 'Save failed' }),
      },
    );

  const doSave = () =>
    run(
      {
        ...draft,
        emails: emailsText
          .split(/[,\n;]+/)
          .map((e) => e.trim().toLowerCase())
          .filter(Boolean),
      },
      'Saved.',
    );

  return (
    <div className="space-y-3">
      <FormRow label="Reminders">
        <div className="flex h-9 items-center gap-3">
          <Switch on={draft.enabled} onChange={(v) => edit((d) => ({ ...d, enabled: v }))} label="Send order reminders" />
          <span className="text-sm text-brix-ink">{draft.enabled ? 'On' : 'Off'}</span>
        </div>
      </FormRow>

      {draft.enabled && (
        <>
          <FormRow
            label="When"
            hint={
              draft.mode === 'before_delivery'
                ? hasSchedule
                  ? `The day before each delivery day. It has to land before the ${cutoffLabel ?? '4:00 PM'} PT order cutoff.`
                  : 'Needs delivery days first — set them on the Delivery days tab.'
                : undefined
            }
          >
            <Segmented
              value={draft.mode}
              options={[
                { key: 'before_delivery', label: 'Day before each delivery' },
                { key: 'weekly', label: 'On days I pick' },
              ]}
              onChange={(k) => edit((d) => ({ ...d, mode: k }))}
            />
          </FormRow>

          {draft.mode === 'weekly' && (
            <FormRow
              label="Days"
              hint={
                perWeek === 0
                  ? 'Pick at least one day.'
                  : `${perWeek} reminder${perWeek === 1 ? '' : 's'} a week.`
              }
            >
              <DayChips
                value={draft.days}
                onToggle={(name) =>
                  edit((d) => ({
                    ...d,
                    days: d.days.includes(name) ? d.days.filter((x) => x !== name) : [...d.days, name],
                  }))
                }
              />
            </FormRow>
          )}

          <FormRow label="Time">
            <div className="flex flex-wrap items-center gap-2">
              <select
                className={cn(inputCls, 'w-32')}
                value={draft.time}
                onChange={(e) => edit((d) => ({ ...d, time: e.target.value }))}
              >
                {!TIME_SLOTS.some((t) => t.value === draft.time) && (
                  <option value={draft.time}>{draft.time}</option>
                )}
                {TIME_SLOTS.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
              <select
                className={cn(inputCls, 'w-40')}
                value={draft.time_zone}
                onChange={(e) => edit((d) => ({ ...d, time_zone: e.target.value }))}
              >
                {REMINDER_TIME_ZONES.map((z) => (
                  <option key={z.id} value={z.id}>
                    {z.label}
                  </option>
                ))}
              </select>
            </div>
          </FormRow>

          <FormRow label="Send to" hint="Leave blank to use their order contacts, or the Primary email.">
            <input
              className={cn(inputCls, 'w-full max-w-md')}
              placeholder="orders@store.com, manager@store.com"
              value={emailsText}
              onChange={(e) => {
                setEmailsText(e.target.value);
                setDirty(true);
                setMsg(null);
              }}
            />
          </FormRow>
        </>
      )}

      <FormFooter
        canSave={canSave}
        saving={save.isPending}
        onSave={doSave}
        removeLabel={removeLabel && parseOrderReminders(stored) ? removeLabel : null}
        onRemove={() => {
          if (removeLabel && window.confirm(`${removeLabel}?`)) run(null, 'Removed.');
        }}
        msg={msg}
      />
    </div>
  );
}

/* ================================================================== */
/* Stores / locations table                                             */
/* ================================================================== */

interface SettingsRow {
  key: string;
  name: string;
  code: string | null;
  /** Link to the store's own page (stores only). */
  href: string | null;
  /** Whose row is saved: the store's own account, or this account. */
  targetCustomerId: string;
  locationId: string | null;
  schedule: unknown;
  reminders: unknown;
  /** Locations fall back to the account's setting; stores never do. */
  inherits: boolean;
}

function SettingsTable({
  kind,
  rows,
  pageCustomerId,
  accountSchedule,
  accountReminders,
}: {
  kind: 'days' | 'reminders';
  rows: SettingsRow[];
  pageCustomerId: string;
  accountSchedule?: unknown;
  accountReminders?: unknown;
}) {
  const [openKey, setOpenKey] = useState<string | null>(null);
  const cols = 4;

  const acctSched = useMemo(() => parseDeliverySchedule(accountSchedule), [accountSchedule]);
  const acctRem = useMemo(() => parseOrderReminders(accountReminders), [accountReminders]);

  return (
    <PayTable minWidth={620}>
      <thead>
        <tr>
          <Th>{rows[0]?.inherits ? 'Location' : 'Store'}</Th>
          {kind === 'days' ? (
            <>
              <Th>Delivery days</Th>
              <Th>Order by</Th>
            </>
          ) : (
            <>
              <Th>Reminders</Th>
              <Th>Sends to</Th>
            </>
          )}
          <Th right>
            <span className="sr-only">Actions</span>
          </Th>
        </tr>
      </thead>
      <tbody className="divide-y divide-brix-border">
        {rows.map((r) => {
          const open = openKey === r.key;
          const sched = parseDeliverySchedule(r.schedule);
          const rem = parseOrderReminders(r.reminders);
          const effSched = sched ?? (r.inherits ? acctSched : null);
          const effRem = rem ?? (r.inherits ? acctRem : null);
          const own = kind === 'days' ? Boolean(sched) : Boolean(rem);

          return (
            <Fragment key={r.key}>
              <tr className={cn(open && 'bg-brix-surface-alt/40')}>
                <Td>
                  <div className="font-medium text-brix-ink">
                    {r.href ? (
                      <Link to={r.href} className="hover:underline">
                        {r.name}
                      </Link>
                    ) : (
                      r.name
                    )}
                  </div>
                  {r.code && <div className="text-[11px] text-brix-muted font-mono">{r.code}</div>}
                </Td>
                {kind === 'days' ? (
                  <>
                    <Td>
                      <div className="flex flex-wrap items-center gap-1.5">
                        {effSched ? (
                          <Tag tone={effSched.enabled ? 'muted' : 'off'}>
                            {effSched.enabled ? describeSchedule(effSched) : 'Off'}
                          </Tag>
                        ) : (
                          <Tag tone="off">Not set</Tag>
                        )}
                        {r.inherits && (own ? <Tag tone="own">Own</Tag> : effSched ? <Tag>Account&apos;s</Tag> : null)}
                      </div>
                    </Td>
                    <Td className="text-brix-muted whitespace-nowrap">
                      {effSched?.enabled ? `${fmtCutoffTime(effSched)} PT` : '—'}
                    </Td>
                  </>
                ) : (
                  <>
                    <Td>
                      <div className="flex flex-wrap items-center gap-1.5">
                        <Tag tone={effRem?.enabled ? 'on' : 'off'}>{describeReminders(effRem)}</Tag>
                        {r.inherits && (own ? <Tag tone="own">Own</Tag> : effRem ? <Tag>Account&apos;s</Tag> : null)}
                      </div>
                    </Td>
                    <Td className="text-xs text-brix-muted">
                      {effRem?.enabled ? (effRem.emails.length ? effRem.emails.join(', ') : 'Order contacts') : '—'}
                    </Td>
                  </>
                )}
                <ActionsTd>
                  <Button variant="secondary" size="sm" onClick={() => setOpenKey(open ? null : r.key)}>
                    {open ? 'Close' : 'Edit'}
                  </Button>
                </ActionsTd>
              </tr>
              {open && (
                <ExpandedRow colSpan={cols}>
                  <div className="rounded-lg border border-brix-border bg-brix-surface p-4">
                    {kind === 'days' ? (
                      <ScheduleForm
                        customerId={r.targetCustomerId}
                        pageCustomerId={r.targetCustomerId !== pageCustomerId ? pageCustomerId : undefined}
                        locationId={r.locationId}
                        stored={r.schedule}
                        removeLabel={r.inherits ? 'Use the account’s days' : 'Remove delivery days'}
                      />
                    ) : (
                      <RemindersForm
                        customerId={r.targetCustomerId}
                        pageCustomerId={r.targetCustomerId !== pageCustomerId ? pageCustomerId : undefined}
                        locationId={r.locationId}
                        stored={r.reminders}
                        hasSchedule={Boolean(effSched?.enabled)}
                        cutoffLabel={effSched ? fmtCutoffTime(effSched) : null}
                        removeLabel={r.inherits ? 'Use the account’s reminders' : null}
                      />
                    )}
                  </div>
                </ExpandedRow>
              )}
            </Fragment>
          );
        })}
      </tbody>
    </PayTable>
  );
}

/* ================================================================== */
/* Preview                                                              */
/* ================================================================== */

/** Which rung of the recipients ladder answered — so the address is explainable. */
const RECIPIENT_SOURCE: Record<string, string> = {
  schedule: 'the reminder’s own addresses',
  edi_notify_list: 'the EDI notify list',
  primary_email: 'the Primary email',
  none: 'no address on file',
};

/**
 * What the next reminder would look like, who gets it and when. Read-only:
 * opening it never claims a reminder slot, so it can't stop a real one.
 */
function PreviewSection({ customerId }: { customerId: string }) {
  const [open, setOpen] = useState(false);
  const { data, isLoading, error } = useReminderPreview(customerId, open);

  return (
    <Section
      title="Preview"
      desc="The next reminder for each location of this account, exactly as it would arrive. Nothing is sent."
      actions={
        <Button variant="secondary" size="sm" onClick={() => setOpen((v) => !v)}>
          <Eye size={13} />
          {open ? 'Hide preview' : 'Show preview'}
        </Button>
      }
    >
      {open &&
        (isLoading ? (
          <p className="flex items-center gap-2 text-sm text-brix-muted">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </p>
        ) : error ? (
          <p className="text-sm text-red-700">
            Couldn&apos;t build the preview: {error instanceof Error ? error.message : 'error'}
          </p>
        ) : (data?.rows.length ?? 0) === 0 ? (
          <p className="text-sm text-brix-muted">No active locations to preview.</p>
        ) : (
          <div className="space-y-3">
            {data!.rows.map((row) => (
              <div key={row.location_id} className="overflow-hidden rounded-lg border border-brix-border">
                <div className="grid gap-x-6 gap-y-1 bg-brix-surface-alt/60 px-3 py-2.5 text-xs sm:grid-cols-[1fr_auto]">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold text-brix-ink">{row.location_name}</span>
                    {row.location_code && <span className="font-mono text-brix-muted">{row.location_code}</span>}
                    <Tag tone={row.would_send ? 'on' : 'off'}>{row.would_send ? 'Would send' : 'Nothing would send'}</Tag>
                  </div>
                  <div className="text-brix-muted sm:text-right">{row.reminders}</div>
                  <div className="text-brix-muted sm:col-span-2">
                    {row.reason ? (
                      row.reason
                    ) : (
                      <>
                        Next: <strong className="text-brix-ink">{fmtScheduleDate(row.send_date!)}</strong> at{' '}
                        {row.send_time}
                        {row.delivery_date ? <> · for the {fmtScheduleDate(row.delivery_date)} delivery</> : null}
                      </>
                    )}
                  </div>
                  {row.recipients && row.recipients.length > 0 && (
                    <div className="text-brix-muted sm:col-span-2">
                      To <strong className="text-brix-ink">{row.recipients.join(', ')}</strong> (
                      {RECIPIENT_SOURCE[row.recipient_source ?? 'none']})
                    </div>
                  )}
                </div>
                {row.html && (
                  <iframe
                    title={`Reminder preview — ${row.location_name}`}
                    srcDoc={row.html}
                    sandbox=""
                    className="h-[420px] w-full border-0 bg-white"
                  />
                )}
              </div>
            ))}
          </div>
        ))}
    </Section>
  );
}
